import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarClock, CheckCircle2, Clock, Gavel, Plus, Users, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { backLabel, fmtDate, offLabel } from "@/lib/constants";
import { RecurringAlert } from "@/components/fleet/RecurringAlert";
import { MilestoneShoutouts } from "@/components/slots/Milestones";
import { useAuth } from "@/context/AuthContext";
import { WaitingJobs } from "@/components/jobs/WaitingJobs";
import { OutcomeBadge, StatusBadge } from "@/components/StatusBadge";

const Stat = ({ icon: Icon, label, value, tone, testId, to }) => (
  <Link to={to} data-testid={testId} className={`rise-in group rounded-2xl border bg-card p-5 transition-colors hover:border-primary/50 ${tone}`}>
    <div className="flex items-center justify-between text-muted-foreground">
      <span className="text-xs font-semibold uppercase tracking-widest">{label}</span>
      <Icon className="h-4 w-4" />
    </div>
    <div className="mt-4 font-display text-4xl font-extrabold">{value}</div>
  </Link>
);

const Panel = ({ title, children, testId, action }) => (
  <section data-testid={testId} className="rounded-2xl border bg-card">
    <div className="flex items-center justify-between border-b px-5 py-4">
      <h3 className="text-base font-bold">{title}</h3>
      {action}
    </div>
    <div className="divide-y">{children}</div>
  </section>
);

const Empty = ({ text }) => <p className="px-5 py-8 text-sm text-muted-foreground">{text}</p>;

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, []);

  if (!d) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  const c = d.counts;
  const canAssess = ["admin", "assessor"].includes(user.role);

  return (
    <div className="space-y-10">
      {(user.permissions?.jobs || 0) >= 3 && <WaitingJobs />}
      <div className="on-photo relative overflow-hidden rounded-3xl border">
        <img src="/brand/slider2-1.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#111317]/75" />
        <div className="relative flex flex-col gap-6 p-6 sm:p-10 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Dashboard</p>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">Hello, {user.name}</h1>
            <p className="mt-2 max-w-lg text-sm text-zinc-300">Re-assessments are due {d.reassess_years} years after a successful check ride and flagged {d.due_soon_days} days before.</p>
          </div>
          {canAssess && (
            <button data-testid="dashboard-new-assessment-btn" onClick={() => nav("/assess/new")} className="inline-flex items-center gap-2 self-start rounded-full bg-primary px-6 py-3 font-bold text-primary-foreground transition-shadow hover:shadow-[0_0_24px_rgba(254,220,25,0.4)] md:self-auto">
              <Plus className="h-4 w-4" /> Start check ride
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-6">
        <Stat icon={Users} label="Active riders" value={d.total} testId="stat-total" to="/riders" />
        <Stat icon={CheckCircle2} label="Current" value={c.current || 0} testId="stat-current" to="/riders?status=current" />
        <Stat icon={CalendarClock} label="Due soon" value={c.due_soon || 0} testId="stat-due-soon" to="/riders?status=due_soon" tone="border-l-4 border-l-primary" />
        <Stat icon={AlertTriangle} label="Overdue" value={c.overdue || 0} testId="stat-overdue" to="/riders?status=overdue" tone="border-l-4 border-l-destructive" />
        <Stat icon={Clock} label="Awaiting" value={c.awaiting || 0} testId="stat-awaiting" to="/riders?status=awaiting" />
        <Stat icon={Gavel} label="Needs attention" value={(c.referred || 0) + (c.unsuccessful || 0) + (c.cancelled || 0) + (c.discontinued || 0)} testId="stat-attention" to="/riders?status=referred" />
      </div>

      <MilestoneShoutouts />
      <RecurringAlert items={d.recurring} testid="dash-recurring-alert" />

      <div className="grid gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-7">
          <Panel title="Re-assessments due" testId="panel-due">
            {d.due.length === 0 ? <Empty text="No riders due or overdue. All good." /> : d.due.map((r) => (
              <Link key={r.id} to={`/riders/${r.id}`} data-testid={`due-row-${r.id}`} className="flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-secondary/50">
                <div className="min-w-0">
                  <div className="truncate font-semibold">{r.first_name} {r.last_name}</div>
                  <div className="text-xs text-muted-foreground">Due {fmtDate(r.next_due_date)} · {r.days_until_due < 0 ? `${-r.days_until_due} days overdue` : `in ${r.days_until_due} days`}</div>
                </div>
                <StatusBadge status={r.status} />
              </Link>
            ))}
          </Panel>
          <Panel title="Recent assessments" testId="panel-recent" action={<Link to="/assessments" className="text-sm text-primary hover:underline">View all</Link>}>
            {d.recent.length === 0 ? <Empty text="No assessments recorded yet." /> : d.recent.map((a) => (
              <Link key={a.id} to={`/assessments/${a.id}`} data-testid={`recent-row-${a.id}`} className="flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-secondary/50">
                <div className="min-w-0">
                  <div className="truncate font-semibold">{a.rider_name}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(a.date)} · by {a.assessor_name}</div>
                </div>
                <OutcomeBadge outcome={a.outcome} />
              </Link>
            ))}
          </Panel>
        </div>
        <div className="space-y-6 lg:col-span-5">
          <Panel title={`Bike faults (${d.faults.length} open)`} testId="panel-faults" action={<Link to="/fleet" className="text-sm text-primary hover:underline">Fleet</Link>}>
            {d.off_road.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 bg-red-500/5 px-5 py-3">
                <span className="font-semibold"><Wrench className="mr-2 inline h-4 w-4 text-red-400" />{b.name}</span>
                <span className="text-right text-xs font-bold text-red-300">{offLabel(b)}<span className="block text-[10px] font-semibold text-sky-300">{backLabel(b)}</span></span>
              </div>
            ))}
            {d.faults.length === 0 && d.off_road.length === 0 ? <Empty text="No open bike faults." /> : d.faults.slice(0, 6).map((f) => (
              <Link key={f.id} to={f.run_id ? `/runs/${f.run_id}` : `/fleet/bikes/${f.bike_id}`} data-testid={`dash-fault-${f.id}`} className="block px-5 py-3 hover:bg-secondary/50">
                <div className="truncate text-sm font-semibold">{f.bike_name} · {f.item_label}</div>
                <div className="truncate text-xs text-muted-foreground">{f.note} · {f.reported_by} · {fmtDate(f.reported_at)}</div>
              </Link>
            ))}
          </Panel>
          {d.open_appeals > 0 && user.role !== "assessor" && (
            <Link to="/appeals" data-testid="dash-open-appeals" className="block rounded-2xl border border-amber-500/40 bg-amber-500/5 px-5 py-4 text-sm font-semibold text-amber-200">{d.open_appeals} open appeal{d.open_appeals > 1 ? "s" : ""} / complaint{d.open_appeals > 1 ? "s" : ""}</Link>
          )}
          <Panel title="Referred to Trustees" testId="panel-referred" action={<Gavel className="h-4 w-4 text-amber-400" />}>
            {d.referred.length === 0 ? <Empty text="No referrals awaiting a decision." /> : d.referred.map((a) => (
              <Link key={a.id} to={`/assessments/${a.id}`} className="flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-secondary/50">
                <div className="truncate font-semibold">{a.rider_name}</div>
                <span className="text-xs text-muted-foreground">{fmtDate(a.date)}</span>
              </Link>
            ))}
          </Panel>
          <Panel title="Awaiting first assessment" testId="panel-awaiting">
            {d.awaiting.length === 0 ? <Empty text="No riders waiting." /> : d.awaiting.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-5 py-3">
                <Link to={`/riders/${r.id}`} className="truncate font-semibold hover:text-primary">{r.first_name} {r.last_name}</Link>
                {canAssess && <Link to={`/assess/new?rider=${r.id}`} data-testid={`assess-awaiting-${r.id}`} className="text-sm font-semibold text-primary hover:underline">Assess</Link>}
              </div>
            ))}
          </Panel>
        </div>
      </div>
    </div>
  );
}
